export const IDBIsClosingError: { err: Error | null } = { err: null };

export class Store {
  dbp: Promise<IDBDatabase> | null;
  dbName: string;
  storeName: string;

  constructor(dbName = 'keyval-store', storeName = 'keyval') {
    this.dbName = dbName;
    this.storeName = storeName;
    this.dbp = this._open();
  }

  _open() {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const openreq = indexedDB.open(this.dbName, 1);
      openreq.onerror = () => reject(openreq.error);
      openreq.onsuccess = () => resolve(openreq.result);

      // First time setup: create an empty object store
      openreq.onupgradeneeded = () => {
        openreq.result.createObjectStore(this.storeName);
      };
    });
  }

  _withIDBStore(type: IDBTransactionMode, callback: (store: IDBObjectStore) => void): Promise<void> {
    if (!this.dbp) {
      this.dbp = this._open();
    }
    return this.dbp.then(
      db =>
        new Promise<void>(async (resolve, reject) => {
          let transaction: IDBTransaction;
          try {
            transaction = db.transaction(this.storeName, type);
          } catch (e) {
            // So there seems to be a bug in some IndexedDB implementations where the connection to the
            // database can close without us explicitly closing it. That will cause a sync error being
            // thrown when calling db.transaction(). So we catch it, and bubble this error to `local-db`
            // which will return null to the caller. And on the next use of the database we try to reopen
            // the connection.
            this.dbp = null;
            IDBIsClosingError.err = e;
            reject(IDBIsClosingError);
            return;
          }
          transaction.oncomplete = () => resolve();
          transaction.onabort = transaction.onerror = () => reject(transaction.error);
          callback(transaction.objectStore(this.storeName));
        }),
    );
  }
}

export function get<Type>(key: IDBValidKey, storeToUse: Store): Promise<Type> {
  let req: IDBRequest;
  return storeToUse
    ._withIDBStore('readonly', s => {
      req = s.get(key);
    })
    .then(() => (req.result === undefined ? null : req.result));
}

export function set(key: IDBValidKey, value: any, storeToUse: Store): Promise<void> {
  return storeToUse._withIDBStore('readwrite', s => {
    s.put(value, key);
  });
}

export function del(key: IDBValidKey, storeToUse: Store): Promise<void> {
  return storeToUse._withIDBStore('readwrite', s => {
    s.delete(key);
  });
}

export function clear(storeToUse: Store): Promise<void> {
  return storeToUse._withIDBStore('readwrite', s => {
    s.clear();
  });
}

export function keys(storeToUse: Store): Promise<Array<IDBValidKey | IDBKeyRange>> {
  const validKeys: Array<IDBValidKey | IDBKeyRange> = [];

  return storeToUse
    ._withIDBStore('readonly', s => {
      // This would be store.getAllKeys(), but it isn't supported by Edge or Safari.
      // And openKeyCursor isn't supported by Safari.
      (s.openKeyCursor || s.openCursor).call(s).onsuccess = function() {
        if (!this.result) {
          return;
        }
        validKeys.push(this.result.key);
        this.result.continue();
      };
    })
    .then(() => validKeys);
}
